import { Dropdown, type Option } from "@hoylu/client-common";
import DefaultStyling from "@hoylu/client-common/dist/assets/css.modules/default.dropdown.module.css";
import debug from "debug";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import {
  selectIsUpdatingWorkspace,
  selectPublicPermissionLevel,
} from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { useModalContext } from "../ModalContext";
import Styles from "./PublicSharingSection.module.css";
import { ShareType } from "./ShareType";

const debugLog = debug("hoylu:dashboard:workspace-sharing");

interface PublicSharingProps {
  updateGlobalAccess: (sharingOption: ShareOptionValue) => void;
}

export const PublicSharing = ({ updateGlobalAccess }: PublicSharingProps) => {
  const { modalRef } = useModalContext();

  const tPublicSharing = useI18n("SHARE_WORKSPACE_DIALOG.PUBLIC_SHARING.");
  const tPublicSharingShareType = useI18n(
    "SHARE_WORKSPACE_DIALOG.PUBLIC_SHARING.SHARE_TYPE.",
  );

  const options: Option<number>[] = useMemo(
    () => [
      {
        label: tPublicSharingShareType(ShareType.READ.title),
        value: ShareType.READ.value,
      },
      {
        label: tPublicSharingShareType(ShareType.EDIT.title),
        value: ShareType.EDIT.value,
      },
    ],
    [tPublicSharingShareType],
  );

  const publicPermissionLevel = useSelector(selectPublicPermissionLevel);
  const isUpdatingWorkspace = useSelector(selectIsUpdatingWorkspace);

  const allowPublicAccess =
    publicPermissionLevel.value !== ShareType.NO_ACCESS.value;
  const readOrWriteOption =
    options[publicPermissionLevel.value === ShareType.EDIT.value ? 1 : 0];

  const onChange = (
    allowPublicAccess: boolean,
    readOrWriteOption: Option<number>,
  ) => {
    try {
      let shareOption: ShareOptionValue;

      if (allowPublicAccess) {
        if (readOrWriteOption.value === ShareType.EDIT.value) {
          shareOption = ShareType.EDIT;
        } else {
          shareOption = ShareType.READ;
        }
      } else {
        shareOption = ShareType.NO_ACCESS;
      }

      if (shareOption.value !== publicPermissionLevel.value) {
        updateGlobalAccess(shareOption);
      }
    } catch (error) {
      debugLog("Failed to update global access.", JSON.stringify(error));
    }
  };

  return (
    <div className={Styles.publicSharingSection}>
      <h2 className={Styles.title}>{tPublicSharing("TITLE")}</h2>

      <div>
        <div className={Styles.row}>
          <label
            className={Styles.allowPublicAccess}
            data-tooltip-id="public-access"
          >
            <input
              checked={allowPublicAccess}
              disabled={isUpdatingWorkspace}
              onChange={(e) => onChange(e.target.checked, readOrWriteOption)}
              type="checkbox"
            />
            <span>{tPublicSharing("NON_MEMBERS")}</span>
            <Tooltip delayHide={500} id="public-access">
              <ul className={Styles.publicAccessList}>
                {tPublicSharing("TOOLTIP.TITLE")}
                {["TOOLTIP.ITEM_1", "TOOLTIP.ITEM_2", "TOOLTIP.ITEM_3"].map(
                  (listItemKey) => (
                    <li key={listItemKey}>{tPublicSharing(listItemKey)}</li>
                  ),
                )}
              </ul>
            </Tooltip>
          </label>

          <Dropdown<number>
            allowReset={false}
            classes={[DefaultStyling.dropdown, Styles.readOrWriteDropdown]}
            onChange={([readOrWriteOption]) =>
              onChange(allowPublicAccess, readOrWriteOption)
            }
            options={options}
            portalContainer={modalRef?.current ?? undefined}
            readonly={!allowPublicAccess || isUpdatingWorkspace}
            title=""
            useMultiSelect={false}
            usePortal
            values={[readOrWriteOption]}
          />
        </div>
      </div>
    </div>
  );
};
